/**
 * What an NFL player is actually paid in each roster state.
 *
 * The CBA pays in weekly game checks, and the state the player is in
 * decides the check:
 *
 *   active roster      base salary / 18 per week, plus any per-game roster bonus
 *   injured reserve    base salary / 18 per week (full pay), unless the year
 *                      carries a split-contract "down" salary, then down / 18
 *   PUP                the same as injured reserve
 *   suspended          nothing for those weeks (one game check forfeited per game)
 *   practice squad     the weekly practice-squad rate: what the period says,
 *                      or the CBA minimum for that season and experience; a
 *                      week the player was elevated pays the active check
 *   released           nothing further; guaranteed salary already due survives,
 *                      and a vested veteran with confirmed termination pay keeps
 *                      the base / 18 check through week 18
 *
 * Whether a contract is split, and its down figure, is a contract term the
 * agent records on the year; nothing here invents it. A season with no
 * practice-squad rate on file prices to null, never a guess.
 *
 * Pure, self-contained (structural types), shared by the app, the MCP and
 * the scraper (generated copy). utils/earnings.ts calls these.
 */

import type { PayState, StandingPeriod } from './standingRules.js'

export type PayPeriod = StandingPeriod

export interface PayYear {
  year: number
  base_salary?: number | null
  per_game_roster_bonus?: number | null
  guaranteed_salary?: number | null
  base_salary_split_down?: number | null
}

export interface PayContract {
  accrued_seasons?: number | null
  current_per_game_roster_bonus?: number | null
}

/** Base salary is paid in 18 weekly instalments (17 games and a bye). */
export const GAME_CHECKS_PER_SEASON = 18

/**
 * Practice-squad weekly pay by season under the 2020 CBA (Article 33): a
 * fixed rate for players with two or fewer accrued seasons, and a
 * negotiable range for veterans with more than two, of which the minimum is
 * what a period pays when no figure is written on it. Checked Sep 2026
 * against Sports Illustrated's published schedule (2026: $13,750 fixed;
 * veterans $18,350 to $22,850). Add the next season from the CBA text once
 * verified; a season missing here yields null, never a guess.
 */
export const PRACTICE_SQUAD_WEEKLY: Record<number, { standard: number; veteranMin: number; veteranMax: number }> = {
  2024: { standard: 12_500, veteranMin: 16_500, veteranMax: 20_500 },
  2025: { standard: 13_000, veteranMin: 17_500, veteranMax: 22_000 },
  2026: { standard: 13_750, veteranMin: 18_350, veteranMax: 22_850 },
}

export function psRateKnown(season: number): boolean {
  return PRACTICE_SQUAD_WEEKLY[season] != null
}

/** Veterans are players with more than two accrued seasons. */
export function practiceSquadWeekly(season: number, accruedSeasons: number | null | undefined): number | null {
  const row = PRACTICE_SQUAD_WEEKLY[season]
  if (!row) return null
  return (accruedSeasons ?? 0) > 2 ? row.veteranMin : row.standard
}

/** The football season a date belongs to: January and February are the previous season's. */
export function seasonOf(dateIso: string): number {
  const d = new Date(dateIso)
  const month = d.getUTCMonth()
  return month <= 1 ? d.getUTCFullYear() - 1 : d.getUTCFullYear()
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

/** Regular-season window used when a period gives dates but no week count. */
function seasonWindow(season: number): { start: Date; end: Date } {
  const start = new Date(Date.UTC(season, 8, 1)) // 1 September
  const end = new Date(start.getTime() + GAME_CHECKS_PER_SEASON * WEEK_MS)
  return { start, end }
}

/**
 * Weeks a period counts for in a season. An explicit `weeks` wins. Otherwise
 * the overlap of the period's dates with the season's 18-week window, in
 * whole weeks, with an open period ending today.
 */
export function weeksInSeason(period: PayPeriod, season: number, today: Date = new Date()): number {
  if (period.weeks != null) return Math.max(0, Math.floor(period.weeks))
  const { start, end } = seasonWindow(season)
  const from = new Date(Math.max(new Date(period.start_date).getTime(), start.getTime()))
  const to = new Date(Math.min((period.end_date ? new Date(period.end_date) : today).getTime(), end.getTime()))
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to <= from) return 0
  return Math.floor((to.getTime() - from.getTime()) / WEEK_MS)
}

/** Elevation dates that fall inside this period and this season's window. */
export function elevatedWeeks(period: PayPeriod, season: number, today: Date = new Date()): number {
  const dates = Array.isArray(period.elevations) ? period.elevations : []
  if (dates.length === 0) return 0
  const { start, end } = seasonWindow(season)
  const from = Math.max(new Date(period.start_date).getTime(), start.getTime())
  const to = Math.min((period.end_date ? new Date(period.end_date) : today).getTime(), end.getTime())
  const weeks = new Set<number>()
  for (const d of dates) {
    const t = new Date(d).getTime()
    if (Number.isNaN(t) || t < from || t >= to) continue
    weeks.add(Math.floor((t - start.getTime()) / WEEK_MS))
  }
  return weeks.size
}

export interface PayLine {
  status: PayState
  weeks: number
  /** Dollars per week for this state, or null when the rate is unknown. */
  weekly: number | null
  /** Per-game roster bonus earned in this period, active roster only. */
  bonus: number
  amount: number | null
  rule: string
}

/** What one period pays under a given contract year. */
export function periodPay(
  period: PayPeriod,
  year: PayYear,
  contract: PayContract,
  today: Date = new Date(),
): PayLine {
  const weeks = weeksInSeason(period, year.year, today)
  const base = year.base_salary ?? null
  const perCheck = base != null ? base / GAME_CHECKS_PER_SEASON : null
  const bonusRate = period.per_game_amount ?? year.per_game_roster_bonus ?? contract.current_per_game_roster_bonus ?? 0
  const line = (weekly: number | null, bonus: number, rule: string, amount?: number | null): PayLine => ({
    status: period.status,
    weeks,
    weekly,
    bonus,
    amount: amount !== undefined ? amount : weekly == null ? null : weekly * weeks + bonus,
    rule,
  })

  switch (period.status) {
    case 'active_roster':
      return line(perCheck, (period.games ?? 0) * bonusRate, 'base salary / 18 per week, plus per-game roster bonus')
    case 'injured_reserve':
    case 'pup': {
      const label = period.status === 'pup' ? 'PUP' : 'IR'
      const down = year.base_salary_split_down
      if (down != null) return line(down / GAME_CHECKS_PER_SEASON, 0, `split contract: down salary / 18 per week on ${label}`)
      return line(perCheck, 0, `full base salary / 18 per week on ${label}`)
    }
    case 'suspended':
      return line(0, 0, 'suspended weeks forfeit the game check')
    case 'practice_squad': {
      const weekly = period.weekly_amount ?? practiceSquadWeekly(year.year, contract.accrued_seasons)
      const rule = period.weekly_amount != null ? 'practice squad, weekly rate on the period' : 'practice squad, CBA weekly minimum for the season'
      const elevated = Math.min(weeks, elevatedWeeks(period, year.year, today))
      if (elevated === 0) return line(weekly, 0, rule)
      // An elevated week pays the active-roster check instead of the PS rate.
      const amount = weekly == null || perCheck == null ? null : weekly * (weeks - elevated) + perCheck * elevated
      return line(weekly, 0, `${rule}; ${elevated} elevated week${elevated === 1 ? '' : 's'} at base / 18`, amount)
    }
    case 'free_agent':
      if (period.termination_pay === true) return line(perCheck, 0, 'termination pay: vested veteran keeps base / 18 through the season')
      return line(0, 0, 'released: base salary stops')
  }
}

export interface YearPay {
  year: number
  lines: PayLine[]
  /** Sum of the lines, or null when any rate was unknown. */
  fromPeriods: number | null
  /** Guaranteed salary is owed regardless of state once the year has started. */
  guaranteedFloor: number
  earned: number | null
}

/**
 * A contract year's earned pay from its roster periods. Guaranteed salary is
 * a floor: a player released in week 6 with a guaranteed year is still owed
 * the guarantee.
 */
export function yearPay(
  year: PayYear,
  periods: PayPeriod[],
  contract: PayContract,
  today: Date = new Date(),
): YearPay {
  const lines = periods
    .filter(p => seasonOf(p.start_date) === year.year)
    .map(p => periodPay(p, year, contract, today))
  const unknown = lines.some(l => l.amount == null)
  const fromPeriods = unknown ? null : lines.reduce((s, l) => s + (l.amount ?? 0), 0)
  const guaranteedFloor = year.guaranteed_salary ?? 0
  const earned = fromPeriods == null ? null : Math.max(fromPeriods, guaranteedFloor)
  return { year: year.year, lines, fromPeriods, guaranteedFloor, earned }
}

/**
 * What one roster period paid, for any screen: with the contract's year on
 * file the CBA rules above apply; a period in a season the contract has no
 * year for falls to what was typed on the period, as the older screens did.
 * Null when the rate cannot be known.
 */
export function periodEarnings(
  period: PayPeriod,
  contract?: (PayContract & { yearly_breakdown?: PayYear[] | null }) | null,
  today: Date = new Date(),
): number | null {
  const year = contract?.yearly_breakdown?.find(y => y.year === seasonOf(period.start_date))
  if (year && contract) return periodPay(period, year, contract, today).amount
  switch (period.status) {
    case 'practice_squad':
    case 'injured_reserve':
    case 'pup':
      return (period.weeks ?? 0) * (period.weekly_amount ?? 0)
    case 'active_roster': {
      const rate = period.per_game_amount ?? contract?.current_per_game_roster_bonus ?? null
      if (rate != null && period.games != null) return period.games * rate
      return (period.weeks ?? 0) * (period.weekly_amount ?? 0)
    }
    default:
      return 0
  }
}
